
import Page from '@/Page.vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { CrudDataStats } from '@/libs/core/+state/models/crud-data-stats';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { CrudQueryPredicate } from '@/libs/core/+state/models/crud-query-predicate';
import { Guid } from '@/libs/common/functions/guid';
import { CycleTimeModel, CycleTimeTypeEnum, ProductCollectionModel } from '@/libs/Api';
import { cycleTimesStore } from '@/libs/cycle-times/+state/store';
import { productCollectionsStore } from '@/libs/products/+state/store';
import CycleTimeDialog from '@/libs/settings/components/CycleTimeDialog.vue';
import LocationSelect from '@/libs/common/components-smart/LocationSelect.vue';
import WorkplaceSelect from '@/libs/common/components-smart/WorkplaceSelect.vue';
import ProductSelect from '@/libs/common/components-smart/ProductSelect.vue';

@Component({
    components: { CycleTimeDialog, LocationSelect, WorkplaceSelect, ProductSelect },
})
export default class CycleTimes extends Page {
    get headers() {
        return [
            { text: this.$i18n.t('cycleTimes.cycleTimeType'), value: 'cycleTimeType', align: 'left', sortable: false },
            { text: this.$i18n.t('locations.code'), value: 'locationCode', align: 'left', sortable: false },
            { text: this.$i18n.t('locations.name'), value: 'location', align: 'left', sortable: false },
            { text: this.$i18n.t('workplaces.code'), value: 'workplaceCode', align: 'left', sortable: false },
            { text: this.$i18n.t('workplaces.name'), value: 'workplace', align: 'left', sortable: false },
            { text: this.$i18n.t('products.code'), value: 'productCode', align: 'left', sortable: false },
            { text: this.$i18n.t('products.name'), value: 'product', align: 'left', sortable: false },
            { text: this.$i18n.t('productCollections.name'), value: 'productCollection', align: 'left', sortable: false },
            { text: this.$i18n.t('cycleTimes.timePerUnitSeconds'), value: 'timePerUnit', align: 'left', sortable: false },
            { text: '', value: 'id', align: 'left', sortable: false },
        ];
    }
    itemsPerPage = 10;
    filterCycleTimeType: Array<CycleTimeTypeEnum> | null = null;
    filterLocation: Array<string> | null = null;
    filterWorkplace: Array<string> | null = null;
    filterProduct: Array<string> | null = null;
    filterProductCollection: Array<string> | null = null;
    selectedItems: CycleTimeModel[] = [];
    bulkEditDialog = false;
    bulkDeleteDialog = false;
    bulkEditValid = true;
    bulkSaving = false;
    bulkDeleting = false;
    bulkTimePerUnitSeconds: number | null = null;

    @Prop() tenantId!: string;
    @Ref() dialog!: CycleTimeDialog;
    @Ref() bulkEditForm!: any;

    @Watch('itemsPerPage')
    itemsPerPageChanged(v: number) {
        window.localStorage.amsItemsPerPage = v.toString();
    }

    @Watch('filterCycleTimeType')
    @Watch('filterLocation')
    @Watch('filterWorkplace')
    @Watch('filterProduct')
    @Watch('filterProductCollection')
    filtersChanged() {
        this.load();
    }

    get loading(): boolean {
        return cycleTimesStore.useGetter(CrudGetter.Pending);
    }

    get items(): CycleTimeModel[] {
        return [...cycleTimesStore.useGetter(CrudGetter.Data)];
    }

    get totalRows(): number {
        return ({ ...cycleTimesStore.useGetter(CrudGetter.DataStats) } as CrudDataStats)?.rowCount;
    }

    get cycleTimeTypes() {
        return [
            { text: this.$t('cycleTimes.type.byWorkplace') as any, value: CycleTimeTypeEnum.ByWorkplace },
            { text: this.$t('cycleTimes.type.byProductAndWorkplace') as any, value: CycleTimeTypeEnum.ByProductAndWorkplace },
            {
                text: this.$t('cycleTimes.type.byProductCollectionAndWorkplace') as any,
                value: CycleTimeTypeEnum.ByProductCollectionAndWorkplace,
            },
            { text: this.$t('cycleTimes.type.byProductAndLocation') as any, value: CycleTimeTypeEnum.ByProductAndLocation },
            {
                text: this.$t('cycleTimes.type.byProductCollectionAndLocation') as any,
                value: CycleTimeTypeEnum.ByProductCollectionAndLocation,
            },
        ];
    }

    get productCollections() {
        const items = (productCollectionsStore.useGetter(CrudGetter.Data) as any[]) ?? [];
        return items.map((x) => ({ id: x.id, label: `${x.name ?? ''}`.trim() })).sort((a, b) => a.label.localeCompare(b.label));
    }

    mounted() {
        this.itemsPerPage = window.localStorage.amsItemsPerPage ? Number(window.localStorage.amsItemsPerPage) : 10;
        this.loadProductCollections();
        this.load();
    }

    loadProductCollections() {
        productCollectionsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<ProductCollectionModel>([{ field: 'name', index: 1, order: 'asc' }])
        );
    }

    load() {
        cycleTimesStore.dispatch(CrudAction.GetAll, this.getPayload());
    }

    getFilters(): CrudQueryPredicate[] {
        const filters: CrudQueryPredicate[] = [];
        
        if (this.filterCycleTimeType && this.filterCycleTimeType.length > 0) {
            const typeFilters: CrudQueryPredicate[] = [];
            this.filterCycleTimeType.forEach((type) => {
                typeFilters.push({
                    field: 'cycleTimeType',
                    op: 'eq',
                    comparand: type,
                    junction: 'or',
                });
            });
            if (typeFilters.length > 0) {
                filters.push({ field: 'wrap', op: 'wrap', comparand: typeFilters });
            }
        }

        if (this.filterLocation && this.filterLocation.length > 0) {
            const locationFilters: CrudQueryPredicate[] = [];
            this.filterLocation.forEach((locationId) => {
                locationFilters.push({
                    field: 'locationId',
                    op: 'eq',
                    comparand: locationId,
                    junction: 'or',
                });
            });
            if (locationFilters.length > 0) {
                filters.push({ field: 'wrap', op: 'wrap', comparand: locationFilters });
            }
        }

        if (this.filterWorkplace && this.filterWorkplace.length > 0) {
            const workplaceFilters: CrudQueryPredicate[] = [];
            this.filterWorkplace.forEach((workplaceId) => {
                workplaceFilters.push({
                    field: 'workplaceId',
                    op: 'eq',
                    comparand: workplaceId,
                    junction: 'or',
                });
            });
            if (workplaceFilters.length > 0) {
                filters.push({ field: 'wrap', op: 'wrap', comparand: workplaceFilters });
            }
        }

        if (this.filterProduct && this.filterProduct.length > 0) {
            const productFilters: CrudQueryPredicate[] = [];
            this.filterProduct.forEach((productId) => {
                productFilters.push({
                    field: 'productId',
                    op: 'eq',
                    comparand: productId,
                    junction: 'or',
                });
            });
            if (productFilters.length > 0) {
                filters.push({ field: 'wrap', op: 'wrap', comparand: productFilters });
            }
        }

        if (this.filterProductCollection && this.filterProductCollection.length > 0) {
            const collectionFilters: CrudQueryPredicate[] = [];
            this.filterProductCollection.forEach((collectionId) => {
                collectionFilters.push({
                    field: 'productCollectionId',
                    op: 'eq',
                    comparand: collectionId,
                    junction: 'or',
                });
            });
            if (collectionFilters.length > 0) {
                filters.push({ field: 'wrap', op: 'wrap', comparand: collectionFilters });
            }
        }

        return filters;
    }

    getPayload() {
        return createCrudQueryPayload<CycleTimeModel>([
            { field: 'cycleTimeType', index: 1, order: 'asc' },
            { field: 'createdAt', index: 2, order: 'desc' },
        ], this.getFilters());
    }

    open(item: CycleTimeModel) {
        this.dialog.open(item.id!);
    }

    createItemClick() {
        this.dialog.open(Guid.EmptyGuid());
    }

    translateType(t?: CycleTimeTypeEnum) {
        switch (t) {
            case CycleTimeTypeEnum.ByWorkplace: return this.$t('cycleTimes.type.byWorkplace');
            case CycleTimeTypeEnum.ByProductAndWorkplace: return this.$t('cycleTimes.type.byProductAndWorkplace');
            case CycleTimeTypeEnum.ByProductCollectionAndWorkplace: return this.$t('cycleTimes.type.byProductCollectionAndWorkplace');
            case CycleTimeTypeEnum.ByProductAndLocation: return this.$t('cycleTimes.type.byProductAndLocation');
            case CycleTimeTypeEnum.ByProductCollectionAndLocation: return this.$t('cycleTimes.type.byProductCollectionAndLocation');
            default: return '';
        }
    }

    formatSeconds(ct?: string | null) {
        if (!ct) return '';
        // supports "PTnHnMnS" and "HH:MM:SS" strings -> return seconds string
        try {
            const iso = String(ct).trim();
            if (/^P/i.test(iso)) {
                const m = /P(?:\d+D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:[.,]\d+)?)S)?/i.exec(iso);
                if (!m) return '';
                const hh = Number(m[1] || 0);
                const mm = Number(m[2] || 0);
                const ss = Number((m[3] || '0').replace(',', '.'));
                return ((hh * 3600) + (mm * 60) + ss).toString();
            }
            // d.hh:mm:ss[.fff]
            const md = /^(\d+)\.(\d{1,2}):(\d{2}):(\d{2})(?:[.,](\d+))?$/.exec(iso);
            if (md) {
                const d = Number(md[1]);
                const hh = Number(md[2]);
                const mm = Number(md[3]);
                const ss = Number(md[4]) + Number(md[5] ? '0.' + md[5] : 0);
                return (d * 86400 + hh * 3600 + mm * 60 + ss).toString();
            }
            const m = /^(\d{1,2}):(\d{2}):(\d{2})(?:[.,](\d+))?$/.exec(iso);
            if (!m) return '';
            const hh = Number(m[1]);
            const mm = Number(m[2]);
            const ss = Number(m[3]) + Number(m[4] ? '0.' + m[4] : 0);
            return ((hh * 3600) + (mm * 60) + ss).toString();
        } catch {
            return '';
        }
    }

    requiredNumberPositiveRule(v: any) {
        return (!!v && Number(v) > 0) || (this.$t('validation.enterAnInteger') as any);
    }

    formatSecondsToTimeSpan(seconds: number | null): string | null {
        if (!seconds || Number(seconds) <= 0) return null;
        const s = Math.floor(Number(seconds));
        const days = Math.floor(s / 86400);
        const rem = s % 86400;
        const hh = Math.floor(rem / 3600);
        const mm = Math.floor((rem % 3600) / 60);
        const sec = rem % 60;
        const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
        const time = `${pad(hh)}:${pad(mm)}:${pad(sec)}`;
        return days > 0 ? `${days}.${time}` : time;
    }

    bulkEditClick() {
        this.bulkTimePerUnitSeconds = null;
        this.bulkEditDialog = true;
    }

    closeBulkEdit() {
        this.bulkEditDialog = false;
        this.bulkTimePerUnitSeconds = null;
    }

    async saveBulkEdit() {
        if (this.bulkEditForm.validate()) {
            this.bulkSaving = true;
            const timePerUnit = this.formatSecondsToTimeSpan(this.bulkTimePerUnitSeconds);
            
            try {
                // Update all selected items
                const updatePromises = this.selectedItems.map((item) => {
                    const updatedItem = { ...item, timePerUnit };
                    return new Promise((resolve, reject) => {
                        cycleTimesStore.dispatch(CrudAction.Update, { item: updatedItem });
                        this.subscribe(cycleTimesStore, CrudReponse.Update)
                            .then(() => resolve(null))
                            .catch((e: any) => reject(e));
                    });
                });

                await Promise.all(updatePromises);
                
                this.bulkSaving = false;
                this.closeBulkEdit();
                this.selectedItems = [];
                this.load();
            } catch (error) {
                this.bulkSaving = false;
            }
        }
    }

    bulkDeleteClick() {
        this.bulkDeleteDialog = true;
    }

    async confirmBulkDelete() {
        this.bulkDeleting = true;
        
        try {
            // Delete all selected items
            const deletePromises = this.selectedItems.map((item) => {
                return new Promise((resolve, reject) => {
                    cycleTimesStore.dispatch(CrudAction.Delete, { id: item.id });
                    this.subscribe(cycleTimesStore, CrudReponse.Delete)
                        .then(() => resolve(null))
                        .catch((e: any) => reject(e));
                });
            });

            await Promise.all(deletePromises);
            
            this.bulkDeleting = false;
            this.bulkDeleteDialog = false;
            this.selectedItems = [];
            this.load();
        } catch (error) {
            this.bulkDeleting = false;
        }
    }
}
